import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { getClipboardTableContent, getClipboardTsvTableContent } from "./clipboard-tables";
import { sanitizeUEditorUrl } from "./url-safety";

export type ClipboardImagesOptions = {
  maxFileSize: number;
  allowedMimeTypes: string[];
  upload?: (file: File) => Promise<string> | string;
  fallbackToDataUrl: boolean;
  insertMode: "base64" | "upload";
};

export const DEFAULT_UEDITOR_IMAGE_MAX_FILE_SIZE = 10 * 1024 * 1024;
export const DEFAULT_UEDITOR_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/svg+xml"];

function getImageFiles(dataTransfer: DataTransfer | null): File[] {
  if (!dataTransfer) return [];

  // Some browsers expose the *same* pasted/dropped image both in `items` and `files`.
  // Prefer `items` when available to avoid duplicate inserts.
  const itemFiles: File[] = [];
  const byKey = new Map<string, File>();

  for (const item of Array.from(dataTransfer.items ?? [])) {
    if (item.kind !== "file") continue;
    if (!item.type.startsWith("image/")) continue;
    const file = item.getAsFile();
    if (!file) continue;
    byKey.set(`${file.name}:${file.size}:${file.lastModified}`, file);
  }

  itemFiles.push(...Array.from(byKey.values()));
  if (itemFiles.length > 0) return itemFiles;

  for (const file of Array.from(dataTransfer.files ?? [])) {
    if (!file.type.startsWith("image/")) continue;
    byKey.set(`${file.name}:${file.size}:${file.lastModified}`, file);
  }

  return Array.from(byKey.values());
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });
}

async function resolveImageSrc(file: File, options: ClipboardImagesOptions): Promise<string> {
  if (options.insertMode === "upload" && options.upload) {
    try {
      const result = await options.upload(file);
      const src = typeof result === "string" ? sanitizeUEditorUrl(result, "image") : "";
      if (src) return src;
    } catch (err) {
      if (!options.fallbackToDataUrl) throw err;
    }
  }

  return fileToDataUrl(file);
}

export const ClipboardImages = Extension.create<ClipboardImagesOptions>({
  name: "clipboardImages",

  addOptions() {
    return {
      maxFileSize: DEFAULT_UEDITOR_IMAGE_MAX_FILE_SIZE,
      allowedMimeTypes: DEFAULT_UEDITOR_IMAGE_MIME_TYPES,
      upload: undefined,
      fallbackToDataUrl: true,
      insertMode: "base64",
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const options = this.options;

    const insertFiles = async (files: File[], selectionPos?: number) => {
      if (selectionPos !== undefined) {
        editor.commands.setTextSelection(selectionPos);
      }

      for (const file of files) {
        if (file.size > options.maxFileSize) continue;
        if (options.allowedMimeTypes.length > 0 && !options.allowedMimeTypes.includes(file.type)) continue;

        try {
          const src = await resolveImageSrc(file, options);
          editor.chain().focus().setImage({ src, alt: file.name }).run();
          editor.commands.createParagraphNear();
        } catch {
          // Upload failed with no fallback — skip this file and continue batch.
        }
      }
    };

    return [
      new Plugin({
        props: {
          handlePaste: (_view, event) => {
            if (!event || !event.clipboardData) return false;

            const tableContent = getClipboardTableContent(event.clipboardData);
            if (tableContent) {
              event.preventDefault();
              editor.chain().focus().insertContent(tableContent).run();
              return true;
            }

            const tsvTableContent = getClipboardTsvTableContent(event.clipboardData);
            if (tsvTableContent) {
              event.preventDefault();
              editor.chain().focus().insertContent(tsvTableContent).run();
              return true;
            }

            const files = getImageFiles(event.clipboardData);
            if (files.length === 0) return false;

            event.preventDefault();
            void insertFiles(files);
            return true;
          },
          handleDrop: (view, event, _slice, moved) => {
            if (moved) return false;
            if (!(event instanceof DragEvent)) return false;
            const files = getImageFiles(event.dataTransfer);
            if (files.length === 0) return false;

            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
            event.preventDefault();
            void insertFiles(files, pos);
            return true;
          },
        },
      }),
    ];
  },
});
